import { useMemo, useState } from "react";
import { collection, getDocs, orderBy, query, QueryConstraint, Timestamp, where } from "firebase/firestore";
import { Alert, Badge, Button, Divider, Group, Modal, ScrollArea, SegmentedControl, Stack, Stepper, Table, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Dropzone } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconDownload, IconFileSpreadsheet, IconListCheck } from "@tabler/icons-react";
import * as XLSX from "xlsx";
import "@mantine/dropzone/styles.css";
import { useFirestore } from "../../helpers/firebaseContext";
import { CtcBulkSettleRow, CtcBulkSettleRowResult, CtcSettlement } from "../../types";
import { formatMoney, useCtcCallables, useCtcUser } from "./ctc_helpers";
import { CtcBulkSettleRulesModal } from "./ctc_bulk_settle_rules";

/*
 * Bulk "Mark settled" from Excel (back office):
 *   1. Upload  — download the unsettled template for a date range, fill Payment Ref (or Comments) + Settled On, upload it
 *   2. Check   — the server matches every row against Firestore (dry run, nothing written)
 *   3. Done    — after confirmation the server re-checks and settles the ready rows
 * One sheet row = one side (Customer / Broker) of one order, same as the per-row buttons.
 */

const MAX_ROWS = 500; // same limit as ctcBulkMarkSettled
const MAX_FILE_SIZE = 5 * 1024 * 1024;
// Extensions as well as MIME types: browsers report CSV inconsistently (text/csv, application/vnd.ms-excel, or empty)
const ACCEPT = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel": [".xls", ".csv"],
    "text/csv": [".csv"],
};
const COLUMNS = { reference: "Reference No", side: "Side", amount: "Amount", currency: "Currency", opened: "Opened", paymentRef: "Payment Ref", comments: "Comments", settledOn: "Settled On" };

const STATUS_COLOR: Record<CtcBulkSettleRowResult["status"], string> = { ready: "teal", settled: "teal", invalid: "red", failed: "red" };
const STATUS_LABEL: Record<CtcBulkSettleRowResult["status"], string> = { ready: "Matched", settled: "Settled", invalid: "Problem", failed: "Failed" };

const ddmmyyyy = (d: Date) => d.toLocaleDateString("en-GB");
/** UTC midnight of a calendar day, or undefined if the day doesn't exist (31/02, month 13…). */
const isoDay = (y: number, m: number, d: number) => {
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d ? date.toISOString() : undefined;
};

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
// Optional time after the date ("14:30", "2:30 PM", "14:30:00"); settlement is per day, so it's dropped
const TIME = String.raw`(?:[ T]+\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?\s*(?:[AaPp][Mm])?)?`;
const DMY = new RegExp(String.raw`^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})${TIME}$`); // 06/10/2026 — always day first
const YMD = new RegExp(String.raw`^(\d{4})-(\d{1,2})-(\d{1,2})${TIME}$`); // 2026-10-06
const D_MON_Y = new RegExp(String.raw`^(\d{1,2})[ -]([A-Za-z]{3,9})[ ,-]+(\d{4})${TIME}$`); // 6 Oct 2026, 06-Oct-2026

/**
 * Excel date cell (serial number, time ignored) or typed text → ISO at UTC midnight, the same
 * instant the single "Settled on" picker sends. Text that isn't one of the formats above, or a day
 * that doesn't exist, is passed through as-is: the server only accepts the ISO form, so it reports
 * the row as an invalid date instead of guessing (e.g. reading 06/10 month-first as 10 June).
 */
const toIsoDate = (value: unknown): string | undefined => {
    if (typeof value === "number") {
        const d = XLSX.SSF.parse_date_code(value);
        return (d && isoDay(d.y, d.m, d.d)) || `${value}`;
    }
    const text = `${value ?? ""}`.trim();
    if (!text) return undefined;
    const dmy = text.match(DMY);
    if (dmy) return isoDay(+dmy[3], +dmy[2], +dmy[1]) ?? text;
    const ymd = text.match(YMD);
    if (ymd) return isoDay(+ymd[1], +ymd[2], +ymd[3]) ?? text;
    const dMonY = text.match(D_MON_Y);
    const month = dMonY ? MONTHS.indexOf(dMonY[2].slice(0, 3).toLowerCase()) : -1;
    if (dMonY && month >= 0) return isoDay(+dMonY[3], month + 1, +dMonY[1]) ?? text;
    return text;
};

const toAmount = (value: unknown): number | undefined => {
    if (typeof value === "number") return value;
    const text = `${value ?? ""}`.replace(/,/g, "").trim();
    return text && Number.isFinite(Number(text)) ? Number(text) : undefined;
};

/**
 * Reads the first sheet of an .xlsx, .xls or .csv; columns are matched by header name, so their
 * order doesn't matter. CSV cells are read as plain text: otherwise the library guesses dates
 * month-first (06/10/2026 → 10 June) before our day-first parser ever sees them.
 */
const parseSheet = async (file: File): Promise<CtcBulkSettleRow[]> => {
    const isCsv = /\.csv$/i.test(file.name) || file.type === "text/csv";
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", raw: isCsv });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!sheet) throw new Error("The file has no sheets");
    const [header = [], ...data] = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, defval: "" });
    const col = (name: string) => header.findIndex((h) => `${h}`.trim().toLowerCase() === name.toLowerCase());
    const idx = { reference: col(COLUMNS.reference), side: col(COLUMNS.side), amount: col(COLUMNS.amount), paymentRef: col(COLUMNS.paymentRef), settledOn: col(COLUMNS.settledOn) };
    const missing = Object.entries(idx).filter(([, i]) => i < 0).map(([key]) => COLUMNS[key as keyof typeof idx]);
    if (missing.length) throw new Error(`Missing column(s): ${missing.join(", ")}. Download the template and keep its header row.`);
    const commentsIdx = col(COLUMNS.comments); // optional: templates downloaded before it was added don't have it

    return data
        .map((cells, i) => ({ cells, row: i + 2 })) // +1 header, +1 for Excel's 1-based rows
        .filter(({ cells }) => cells.some((c) => `${c ?? ""}`.trim() !== ""))
        .map(({ cells, row }) => ({
            row,
            referenceNumber: `${cells[idx.reference] ?? ""}`.trim(),
            side: `${cells[idx.side] ?? ""}`.trim(),
            amount: toAmount(cells[idx.amount]),
            reference: `${cells[idx.paymentRef] ?? ""}`.trim() || undefined,
            comment: commentsIdx >= 0 ? `${cells[commentsIdx] ?? ""}`.trim() || undefined : undefined,
            settledAt: toIsoDate(cells[idx.settledOn]),
        }));
};

function ResultsTable({ results, sheetRows }: { results: CtcBulkSettleRowResult[]; sheetRows: Map<number, CtcBulkSettleRow> }) {
    return (
        <ScrollArea.Autosize mah={420}>
            <Table striped withTableBorder stickyHeader fz="sm">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Row</Table.Th>
                        <Table.Th>Reference</Table.Th>
                        <Table.Th>Side</Table.Th>
                        <Table.Th>Sheet amount</Table.Th>
                        <Table.Th>Database amount</Table.Th>
                        <Table.Th>Payment ref</Table.Th>
                        <Table.Th>Settled on</Table.Th>
                        <Table.Th>Status</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {results.map((r) => {
                        const sheet = sheetRows.get(r.row);
                        const settledOn = sheet?.settledAt ? new Date(sheet.settledAt) : undefined;
                        return (
                            <Table.Tr key={r.row}>
                                <Table.Td>{r.row}</Table.Td>
                                <Table.Td>{r.referenceNumber || "—"}</Table.Td>
                                <Table.Td tt="capitalize">{r.side || "—"}</Table.Td>
                                <Table.Td>{sheet?.amount ?? "—"}</Table.Td>
                                <Table.Td>{r.amount !== undefined ? formatMoney(r.amount, r.currencyId) : "—"}</Table.Td>
                                <Table.Td>
                                    {sheet?.reference ?? "—"}
                                    {sheet?.comment && <Text size="xs" c="dimmed">Comment: {sheet.comment}</Text>}
                                </Table.Td>
                                <Table.Td>{settledOn && !Number.isNaN(settledOn.getTime()) ? ddmmyyyy(settledOn) : sheet?.settledAt ?? "—"}</Table.Td>
                                <Table.Td>
                                    <Badge size="sm" variant="light" color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                                    {r.reason && <Text size="xs" c="red">{r.reason}</Text>}
                                </Table.Td>
                            </Table.Tr>
                        );
                    })}
                    {!results.length && <Table.Tr><Table.Td colSpan={8}><Text size="sm" c="dimmed">No rows to show.</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
            </Table>
        </ScrollArea.Autosize>
    );
}

export function CtcBulkSettleModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
    const db = useFirestore();
    const user = useCtcUser();
    const { bulkMarkSettled } = useCtcCallables();

    const [step, setStep] = useState(0);
    const [range, setRange] = useState<[string | null, string | null]>([null, null]);
    const [downloading, setDownloading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [settling, setSettling] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [fileName, setFileName] = useState("");
    const [sheetRows, setSheetRows] = useState<CtcBulkSettleRow[]>([]);
    const [results, setResults] = useState<CtcBulkSettleRowResult[]>([]);
    const [filter, setFilter] = useState<"all" | "ok" | "problems">("all");
    const [rulesOpen, setRulesOpen] = useState(false);

    const sheetByRow = useMemo(() => new Map(sheetRows.map((r) => [r.row, r])), [sheetRows]);
    const ready = results.filter((r) => r.status === "ready");
    const settled = results.filter((r) => r.status === "settled");
    const problems = results.filter((r) => r.status === "invalid" || r.status === "failed");
    const shown = filter === "all" ? results : filter === "ok" ? results.filter((r) => r.status === "ready" || r.status === "settled") : problems;
    const busy = downloading || checking || settling;

    const reset = () => {
        setStep(0);
        setConfirming(false);
        setFileName("");
        setSheetRows([]);
        setResults([]);
        setFilter("all");
    };

    const close = () => {
        if (busy) return;
        reset();
        onClose();
    };

    /** Template: every unsettled side (amount calculated) of orders opened in the range. */
    const downloadTemplate = async () => {
        const [from, to] = range;
        if (!from || !to) return;
        setDownloading(true);
        try {
            const start = new Date(`${from}T00:00:00`);
            const end = new Date(`${to}T00:00:00`);
            end.setDate(end.getDate() + 1);
            const constraints: QueryConstraint[] = [
                ...(user.customerId ? [where("customerId", "==", user.customerId)] : []),
                where("settled", "==", false),
                where("created", ">=", Timestamp.fromDate(start)),
                where("created", "<", Timestamp.fromDate(end)),
                orderBy("created", "desc"),
            ];
            const snap = await getDocs(query(collection(db, "ctc_settlements"), ...constraints));
            const rows = snap.docs.flatMap((doc) => {
                const s = doc.data() as CtcSettlement;
                if (s.deleted) return []; // order cancelled after completion: nothing to settle
                return (["customer", "broker"] as const)
                    .filter((side) => s[side]?.status === "unsettled" && s[side]?.amount !== undefined)
                    .map((side) => ({
                        [COLUMNS.reference]: s.referenceNumber,
                        [COLUMNS.side]: side === "customer" ? "Customer" : "Broker",
                        [COLUMNS.amount]: s[side].amount,
                        [COLUMNS.currency]: s[side].currencyId ?? "",
                        [COLUMNS.opened]: s.created?.toDate ? ddmmyyyy(s.created.toDate()) : "",
                        [COLUMNS.paymentRef]: "",
                        [COLUMNS.comments]: "",
                        [COLUMNS.settledOn]: "",
                    }));
            });
            if (!rows.length) {
                notifications.show({ color: "orange", message: "No unsettled commissions for orders opened in this date range" });
                return;
            }
            const sheet = XLSX.utils.json_to_sheet(rows, { header: Object.values(COLUMNS) });
            sheet["!cols"] = [{ wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 24 }, { wch: 30 }, { wch: 14 }];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, sheet, "Unsettled");
            XLSX.writeFile(workbook, `ctc-unsettled_${from}_to_${to}.xlsx`, { bookType: "xlsx" });
            if (rows.length > MAX_ROWS) {
                notifications.show({ color: "orange", message: `${rows.length} rows downloaded; upload at most ${MAX_ROWS} at a time` });
            }
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message });
        } finally {
            setDownloading(false);
        }
    };

    /** Step 1 → 2: parse the sheet and dry-run it on the server. */
    const checkFile = async (file: File) => {
        setChecking(true);
        try {
            const rows = await parseSheet(file);
            if (!rows.length) throw new Error("The sheet has no data rows");
            if (rows.length > MAX_ROWS) throw new Error(`The sheet has ${rows.length} rows; upload at most ${MAX_ROWS} at a time`);
            const res = await bulkMarkSettled({ rows, dryRun: true });
            setFileName(file.name);
            setSheetRows(rows);
            setResults(res.data.results);
            setFilter(res.data.results.some((r) => r.status === "invalid") ? "problems" : "all");
            setStep(1);
        } catch (e) {
            notifications.show({ color: "red", title: "Could not check the sheet", message: (e as Error).message });
        } finally {
            setChecking(false);
        }
    };

    /** Step 2 → 3: settle the rows that passed the check (the server checks them again). */
    const settle = async () => {
        setSettling(true);
        try {
            const readyRows = ready.map((r) => sheetByRow.get(r.row)!).filter(Boolean);
            const res = await bulkMarkSettled({ rows: readyRows, dryRun: false });
            setResults(res.data.results);
            const failed = res.data.results.filter((r) => r.status !== "settled").length;
            setFilter(failed ? "problems" : "all");
            setStep(2);
            notifications.show({
                color: failed ? "orange" : "green",
                message: `${res.data.results.length - failed} settled${failed ? `, ${failed} not settled` : ""}`,
            });
        } catch (e) {
            notifications.show({ color: "red", title: "Settlement failed", message: (e as Error).message });
        } finally {
            setSettling(false);
            setConfirming(false);
        }
    };

    const filterControl = (
        <SegmentedControl
            size="xs"
            value={filter}
            onChange={(v) => setFilter(v as typeof filter)}
            data={[
                { value: "all", label: `All (${results.length})` },
                { value: "ok", label: `${step === 2 ? "Settled" : "Matched"} (${step === 2 ? settled.length : ready.length})` },
                { value: "problems", label: `Problems (${problems.length})` },
            ]}
        />
    );

    return (
        <Modal.Root opened={opened} onClose={close} size="70rem" closeOnClickOutside={!busy}>
            <Modal.Overlay />
            <Modal.Content>
                {/* Composed header so "Validation rules" sits beside the close button on every step */}
                <Modal.Header>
                    <Modal.Title>Bulk settle commissions</Modal.Title>
                    <Group gap="xs" wrap="nowrap">
                        <Button size="xs" variant="subtle" leftSection={<IconListCheck size={16} />} onClick={() => setRulesOpen(true)}>
                            Validation rules
                        </Button>
                        <Modal.CloseButton ml={0} />
                    </Group>
                </Modal.Header>
                <Modal.Body>
                    <Stepper active={step} size="xs" iconSize={24} mb="sm" allowNextStepsSelect={false}>
                        <Stepper.Step label="Upload" description="Template or filled sheet" />
                        <Stepper.Step label="Check" description="Match with transactions database" />
                        <Stepper.Step label="Done" description="Settled" />
                    </Stepper>

                    {step === 0 && (
                        <Stack>
                            <Text fw={600} size="sm">1. Download the unsettled template</Text>
                            <Group align="flex-end">
                                <DatePickerInput
                                    type="range"
                                    label="Orders opened between"
                                    placeholder="Pick a date range"
                                    value={range}
                                    onChange={(v) => setRange(v as [string | null, string | null])}
                                    maxDate={new Date()}
                                    valueFormat="DD/MM/YYYY"
                                    w={280}
                                />
                                <Button leftSection={<IconDownload size={16} />} variant="light" onClick={downloadTemplate} loading={downloading} disabled={!range[0] || !range[1] || busy}>
                                    Download template
                                </Button>
                            </Group>
                            <Stack gap={2}>
                                <Text size="xs" c="dimmed">For each row that was paid, fill:</Text>
                                <Text size="xs" c="dimmed">• <b>{COLUMNS.paymentRef}</b> (required). If there is no reference, write the reason in <b>{COLUMNS.comments}</b>. Don't leave both empty.</Text>
                                <Text size="xs" c="dimmed">• <b>{COLUMNS.settledOn}</b> (required, not after today). Day first: 06/10/2026, 06-10-2026, 06.10.2026, 2026-10-06, 6 Oct 2026, or an Excel date. A time after the date is ignored.</Text>
                                <Text size="xs" c="dimmed">Delete rows that were not paid. Don't change Reference No, Side or Amount.</Text>
                            </Stack>

                            <Divider />

                            <Text fw={600} size="sm">2. Upload the filled sheet</Text>
                            <Dropzone
                                onDrop={(files) => files[0] && checkFile(files[0])}
                                onReject={() => notifications.show({ color: "red", message: "Only .xlsx, .xls or .csv files up to 5 MB" })}
                                accept={ACCEPT}
                                maxSize={MAX_FILE_SIZE}
                                maxFiles={1}
                                multiple={false}
                                loading={checking}
                                disabled={busy}
                            >
                                <Group justify="center" gap="sm" mih={90} style={{ pointerEvents: "none" }}>
                                    <IconFileSpreadsheet size={32} stroke={1.5} />
                                    <Stack gap={0}>
                                        <Text size="sm">Drop the .xlsx, .xls or .csv here or click to choose</Text>
                                        <Text size="xs" c="dimmed">At most {MAX_ROWS} rows. Nothing is settled until you confirm.</Text>
                                    </Stack>
                                </Group>
                            </Dropzone>
                        </Stack>
                    )}

                    {step === 1 && (
                        <Stack>
                            <Group justify="space-between">
                                <Text size="sm"><b>{fileName}</b> · {results.length} rows · <Text span c="teal" fw={600}>{ready.length} matched with the transactions database</Text>{problems.length > 0 && <> · <Text span c="red" fw={600}>{problems.length} with problems</Text></>}</Text>
                                {filterControl}
                            </Group>
                            {problems.length > 0 && (
                                <Alert color="orange" icon={<IconAlertTriangle size={16} />} p="xs">
                                    Rows with problems will be skipped. Fix them in the sheet and upload again, or settle the ready rows now.
                                </Alert>
                            )}
                            <ResultsTable results={shown} sheetRows={sheetByRow} />

                            {confirming ? (
                                <Alert color="teal" title={`Mark ${ready.length} commission${ready.length === 1 ? "" : "s"} as settled?`}>
                                    <Text size="sm" mb="sm">
                                        This records the payment reference (or comment) and date on each order. Each row is checked again before it is saved. You can undo a single row afterwards from the Transactions list.
                                    </Text>
                                    <Group justify="flex-end">
                                        <Button variant="default" onClick={() => setConfirming(false)} disabled={settling}>Cancel</Button>
                                        <Button color="teal" onClick={settle} loading={settling}>Yes, settle {ready.length}</Button>
                                    </Group>
                                </Alert>
                            ) : (
                                <Group justify="space-between">
                                    <Button variant="default" onClick={reset} disabled={busy}>Upload another file</Button>
                                    <Button color="teal" onClick={() => setConfirming(true)} disabled={!ready.length || busy}>
                                        Settle {ready.length} row{ready.length === 1 ? "" : "s"}
                                    </Button>
                                </Group>
                            )}
                        </Stack>
                    )}

                    {step === 2 && (
                        <Stack>
                            <Group justify="space-between">
                                <Text size="sm"><Text span c="teal" fw={600}>{settled.length} settled</Text>{problems.length > 0 && <> · <Text span c="red" fw={600}>{problems.length} not settled</Text></>}</Text>
                                {filterControl}
                            </Group>
                            <ResultsTable results={shown} sheetRows={sheetByRow} />
                            <Group justify="space-between">
                                <Button variant="default" onClick={reset}>Upload another file</Button>
                                <Button onClick={close}>Close</Button>
                            </Group>
                        </Stack>
                    )}
                    <CtcBulkSettleRulesModal opened={rulesOpen} onClose={() => setRulesOpen(false)} />
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
}
